<script>
  import { beforeUpdate } from 'svelte'
  import { stringifyStyle, calcPxNum, calcBorder } from './utils'

  // basic css style
  export let name = ''
  export let display = undefined
  export let direction = undefined
  export let align = undefined
  export let xAlign = undefined

  export let margin = undefined // number or string or {}, keys: top, bottom, left, right, horizontal, vertical
  export let topMargin = undefined
  export let bottomMargin = undefined
  export let leftMargin = undefined
  export let rightMargin = undefined

  export let padding = undefined // same as margin
  export let topPadding = undefined
  export let bottomPadding = undefined
  export let leftPadding = undefined
  export let rightPadding = undefined

  export let width = undefined
  export let height = undefined
  export let minWidth = undefined
  export let minHeight = undefined
  export let maxWidth = undefined
  export let maxHeight = undefined

  export let border = undefined // string or {}, keys: width, style, color
  export let borderRadius = undefined // string or num ??
  export let borderColor = undefined
  export let topBorder = undefined
  export let bottomBorder = undefined
  export let leftBorder = undefined
  export let rightBorder = undefined

  export let boxShadow = undefined
  export let boxSizing = undefined
  //export let boxSizing = 'border-box' // make sure set border-box in global.css
  export let aspectRatio = undefined
  export let transform = undefined

  //colors 
  export let color = undefined
  export let backgroundColor = undefined
  export let hoverColor = undefined
  export let hoverBgColor = undefined
  export let activeColor = undefined
  export let activeBgColor = undefined

  export let backgroundImage = undefined
  export let backgroundAttachment = undefined
  export let fontSize = undefined
  export let fontFamily = undefined
  export let bold = false
  export let fontWeight = undefined
  export let lineHeight = undefined
  export let textAlign = undefined
  export let verticalAlign = undefined

  // boolean styles
  export let scrollX = false
  export let scrollY = false
  export let overflow = undefined
  export let overflowX = undefined
  export let overflowY = undefined
  export let expand = 0  // flex-grow
  export let hidden = false
  export let editable = false
  export let selectable = true
  export let draggable = false
  export let size = undefined // flex-basis: auto or 100px

  // other styles
  export let position = undefined
  export let top = undefined
  export let bottom = undefined
  export let left = undefined
  export let right = undefined
  export let zIndex = undefined
  export let cursor = undefined

  // for binding when editable
  export let canFocus = false
  export let pointerEvents = undefined

  // binding values
  // clientWH bind will generate empty iframe, don't use this too offen
  export let bindSize = false
  export let clientWidth = 0
  export let clientHeight = 0
  export let divElement = null

  let style = {}

  function calcMp(v) {
    if (typeof(v) == 'number' || !isNaN(Number(v))) {
      return `${v}px`
    } else if (typeof(v) == 'string') {
      return v
    } else if (typeof(v) == 'object' && v) {
      if ('horizontal' in v) v.left = v.right = v.horizontal
      if ('vertical' in v) v.top = v.bottom = v.vertical

      let keys = ['top', 'right', 'bottom', 'left']
      let pxs = []
      for (let key of keys) {
        if (key in v) {
          let px = v[key]
          pxs.push(typeof(px) == 'number' ? px + 'px' : px)
        } else {
          pxs.push('0px')
        }
      }
      return pxs.join(' ')
    } else {
      return null
    }
  }

  function calcShadow(v) {
    if (typeof(v) != 'object') 
      return v

    let vs = []
    vs.push('horizontal' in v ? v.horizontal : '0px')
    vs.push('vertical' in v ? v.vertical : '0px')
    if ('blur' in v) vs.push(v.blur)
    if ('spread' in v) vs.push(v.spread)
    if ('color' in v) vs.push(v.color)
    if ('inset' in v && v.inset) vs.push('inset')
    return vs.join(' ')
  }

  function calcStyle() {
    style = {}
    style['display'] = display
    style['flex-direction'] = direction
    style['justify-content'] = align
    style['align-items'] = xAlign

    // object styles
    style['margin'] = calcMp(margin)
    style['margin-top'] = calcPxNum(topMargin)
    style['margin-bottom'] = calcPxNum(bottomMargin)
    style['margin-left'] = calcPxNum(leftMargin)
    style['margin-right'] = calcPxNum(rightMargin)

    style['padding'] = calcMp(padding)
    style['padding-top'] = calcPxNum(topPadding)
    style['padding-bottom'] = calcPxNum(bottomPadding)
    style['padding-left'] = calcPxNum(leftPadding)
    style['padding-right'] = calcPxNum(rightPadding)

    style['border'] = calcBorder(border, borderColor)
    style['border-top'] = calcBorder(topBorder, borderColor)
    style['border-bottom'] = calcBorder(bottomBorder, borderColor)
    style['border-left'] = calcBorder(leftBorder, borderColor)
    style['border-right'] = calcBorder(rightBorder, borderColor)
    style['box-shadow'] = calcShadow(boxShadow)
    style['box-sizing'] = boxSizing
    style['aspect-ratio'] = aspectRatio
    style['transform'] = transform

    // num styles
    style['width'] = calcPxNum(width)
    style['height'] = calcPxNum(height)
    style['min-width'] = calcPxNum(minWidth)
    style['min-height'] = calcPxNum(minHeight)
    style['max-width'] = calcPxNum(maxWidth)
    style['max-height'] = calcPxNum(maxHeight)
    style['border-radius'] = calcPxNum(borderRadius)
    

    // string styles
    if (backgroundImage) {
      style['background-image'] = `url(${backgroundImage})`
      style['background-attachment'] = backgroundAttachment
      style['background-repeat'] = 'no-repeat'
      style['background-size'] = '100% 100%'
    }
    style['font-size'] = calcPxNum(fontSize)
    style['font-family'] = fontFamily
    style['font-weight'] = fontWeight || (bold ? 'bold' : 'normal')
    style['line-height'] = calcPxNum(lineHeight)
    style['text-align'] = textAlign
    style['vertical-align'] = verticalAlign

    // colors
    style['--color'] = color
    style['--bg-color'] = backgroundColor
    style['--hover-color'] = hoverColor || color
    style['--hover-bg-color'] = hoverBgColor || backgroundColor
    style['--active-color'] = activeColor || color
    style['--active-bg-color'] = activeBgColor || backgroundColor

    // boolean styles
    style['overflow'] = overflow
    style['overflow-x'] = scrollX ? 'scroll' : overflowX
    style['overflow-y'] = scrollY ? 'scroll' : overflowY
    style['flex-grow'] = expand > 0 ? expand : null
    style['flex-basis'] = size ? size : null
    style['display'] = hidden ? 'none' : style['display']
    style['user-select'] = selectable ? null : 'none'

    // other styles
    style['position'] = position
    style['top'] = calcPxNum(top)
    style['bottom'] = calcPxNum(bottom)
    style['left'] = calcPxNum(left)
    style['right'] = calcPxNum(right)
    style['z-index'] = zIndex
    style['cursor'] = cursor
    style['pointer-events'] = pointerEvents

    return style
  }

  let props = {}
  beforeUpdate(() => {
    //style = calcStyle() // update when export change outside , what if call calc in html?
    props = {}
    style = stringifyStyle(calcStyle())
    if (style) props.style = style
    if (name) props.name = name
    if (editable || canFocus) props.tabindex = -1
    if (editable) props.contenteditable = 'true'
    if (draggable) props.draggable = 'true'
  })

  
</script>

<style>
  div {
    color: var(--color);
    background-color: var(--bg-color);
    outline: none;
  }
  div:hover {
    color: var(--hover-color);
    background-color: var(--hover-bg-color);
  }
  div:active {
    color: var(--active-color);
    background-color: var(--active-bg-color);
  }
  div:focus {
    border-color: var(--focus-border-color);
  }
</style>

<!-- Fuck clientWidth/Height binding generate many code -->
{#if bindSize}
<div 
  {...props}
  on:click
  on:mousedown
  on:mouseup
  on:mouseover
  on:mouseout
  on:mouseenter
  on:mouseleave
  on:focusin
  on:focusout
  on:focus
  on:blur
  on:contextmenu
  on:drag
  on:dragstart
  on:dragenter
  on:dragover
  on:dragleave
  on:dragend
  on:drop
  bind:clientWidth={clientWidth}
  bind:clientHeight={clientHeight}
  bind:this={divElement}
>
  <slot></slot>
</div>
{:else}
<div 
  {...props}
  on:click
  on:mousedown
  on:mouseup
  on:mouseover
  on:mouseout
  on:mouseenter
  on:mouseleave
  on:focusin
  on:focusout
  on:focus
  on:blur
  on:contextmenu
  on:drag
  on:dragstart
  on:dragenter
  on:dragover
  on:dragleave
  on:dragend
  on:drop
  bind:this={divElement}
>
  <slot></slot>
</div>
{/if}


